import _ from 'lodash'
import { combineReducers } from 'redux'

import config from 'config'
import { UploadStatus } from 'api/enums'
import {
  ARTWORK_FILES_RECEIVE,
  ARTWORK_FILES_PROCESSED,
  ARTWORK_UPLOADS_ADD,
  ARTWORK_UPLOADS_SHOW_DETAILS,
  ARTWORK_UPLOADS_HIDE_DETAILS,
  ARTWORK_UPLOADS_EMPTY,
  ARTWORK_PAGE_CHANGE,
  ARTWORK_ROWS_PER_PAGE_CHANGE,
  LOADING_ARTWORKS,
  LOADING_ARTWORKS_SUCCEEDED,
  LOADING_ARTWORKS_FAILED,
  LOADING_ARTWORK_COUNT_SUCCEEDED,
  CREATING_ARTWORK_SUCCEEDED,
  UPDATING_ARTWORK_SUCCEEDED,
  PUBLISHING_ARTWORK_SUCCEEDED,
  UNPUBLISHING_ARTWORK_SUCCEEDED,
  DELETING_ARTWORK_SUCCEEDED,
  UPLOADING_ARTWORK,
  UPLOADING_ARTWORK_PROGRESS,
  UPLOADING_ARTWORK_SUCCEEDED,
  UPLOADING_ARTWORK_FAILED,
  SIGNING_IN_SUCCEEDED
} from 'redux/actionTypes'

const byId = (state = {}, action) => {
  switch (action.type) {
    case LOADING_ARTWORKS_SUCCEEDED:
    case CREATING_ARTWORK_SUCCEEDED:
    case UPDATING_ARTWORK_SUCCEEDED:
    case PUBLISHING_ARTWORK_SUCCEEDED:
    case UNPUBLISHING_ARTWORK_SUCCEEDED:
      return {
        ...state,
        ...action.payload.entities.artworks
      }

    case DELETING_ARTWORK_SUCCEEDED:
      return _.omit(state, [ Number(action.id), String(action.id) ])

    case SIGNING_IN_SUCCEEDED:
      return {}

    default:
      return state
  }
}

const isFetching = (state = false, action) => {
  switch (action.type) {
    case LOADING_ARTWORKS:
      return true
    case LOADING_ARTWORKS_SUCCEEDED:
    case LOADING_ARTWORKS_FAILED:
      return false
    default:
      return state
  }
}

const paginationInitialState = {
  currentPage: 1,
  rowsPerPage: Number(config.pagination.defaultRowsPerPage)
}

const rowCountByStatus = (state = { '*': 0 }, action) => {
  switch (action.type) {
    case LOADING_ARTWORKS_SUCCEEDED:
    case LOADING_ARTWORK_COUNT_SUCCEEDED:
      if (action.count !== undefined) {
        const status = action.status || '*'
        return {
          ...state,
          [status]: Number(action.count)
        }
      }
      return state
    default:
      return state
  }
}

const pagination = (state = paginationInitialState, action) => {
  switch (action.type) {
    case ARTWORK_PAGE_CHANGE:
      return {
        ...state,
        currentPage: action.page
      }
    case ARTWORK_ROWS_PER_PAGE_CHANGE:
      return {
        ...state,
        rowsPerPage: action.rowsPerPage
      }
    default:
      return state
  }
}

const artworkUploads = (state = {}, action) => {
  switch (action.type) {
    case ARTWORK_UPLOADS_ADD:
      return {
        ...state,
        [action.id]: { ...action, progress: 0, status: UploadStatus.QUEUED }
      }
    case UPLOADING_ARTWORK:
      if (!state[action.id]) {
        return state
      }

      return {
        ...state,
        [action.id]: { ...state[action.id], status: UploadStatus.UPLOADING }
      }
    case UPLOADING_ARTWORK_PROGRESS:
      if (!state[action.id]) {
        return state
      }

      return {
        ...state,
        [action.id]: { ...state[action.id], progress: action.progress }
      }
    case UPLOADING_ARTWORK_SUCCEEDED:
      if (!state[action.id]) {
        return state
      }

      return {
        ...state,
        [action.id]: { ...state[action.id], status: UploadStatus.DONE }
      }
    case UPLOADING_ARTWORK_FAILED:
      if (!state[action.id]) {
        return state
      }

      return {
        ...state,
        [action.id]: { ...state[action.id], status: UploadStatus.FAILED }
      }
    case DELETING_ARTWORK_SUCCEEDED:
      return _.omit(state, [ Number(action.id), String(action.id) ])
    case ARTWORK_UPLOADS_EMPTY:
      return {}
    default:
      return state
  }
}

const artworkUploadStatus = (state = {
  processing: false,
  edit: null
}, action) => {
  switch (action.type) {
    case ARTWORK_FILES_RECEIVE:
      return { processing: true }
    case ARTWORK_FILES_PROCESSED:
      return { processing: false }
    case ARTWORK_UPLOADS_SHOW_DETAILS:
      return {
        ...state,
        edit: action.id
      }
    case ARTWORK_UPLOADS_HIDE_DETAILS:
    case ARTWORK_UPLOADS_EMPTY:
      return {
        ...state,
        edit: null
      }
    default:
      return state
  }
}

const artworksReducer = combineReducers({
  byId,
  isFetching,
  pagination,
  rowCountByStatus,
  artworkUploads,
  artworkUploadStatus
})

export default artworksReducer

// artwork selectors

export const getIsFetching = (state) => state.isFetching

export const getArtwork = (state, id) => {
  let artwork = state.byId[id] || {}

  if (artwork.commissions) {
    artwork.commissions =
      _.sortBy(artwork.commissions, [ 'sizeId', 'frameTypeId' ])
  }

  return artwork
}

export const getArtworks = (state, status = '*') => {
  let ids = Object.keys(state.byId)
  ids = ids.filter(id => getArtwork(state, id).fileName)
  // sort the ids in desceding order
  ids.sort((a, b) => Number(b) - Number(a))
  let artworks = ids.map(id => getArtwork(state, id))
  if (status !== '*') {
    artworks = artworks.filter(artwork => artwork.status === status)
  }
  return artworks
}

// pagination selectors

export const getArtworkPage = (state) => state.pagination.currentPage

export const getArtworkRowsPerPage = (state) => state.pagination.rowsPerPage

export const getArtworkCount = (state, status = '*') =>
  state.rowCountByStatus[status]

// artwork upload selectors

export const getArtworkUploadStatus = (state) => state.artworkUploadStatus

export const getArtworkUpload = (state, id) => state.artworkUploads[id] || {}

export const getArtworkUploads = (state) => {
  let ids = Object.keys(state.artworkUploads)
  // sort the ids in desceding order
  ids.sort((a, b) => Number(b) - Number(a))

  return ids.map(id => getArtworkUpload(state, id))
}

export const getNextArtworkUpload = (state) => {
  return getArtworkUploads(state).find(u => u.status === UploadStatus.QUEUED)
}

export const getUploadProgress = (state) => {
  const uploads = getArtworkUploads(state)

  let total = uploads.length
  let uploaded = 0.0

  uploads.forEach(upload => { uploaded += upload.progress / 100.0 })

  return {
    progress: (uploaded / total).toPrecision(2) * 100,
    uploaded: Math.floor(uploaded),
    total: total
  }
}



// WEBPACK FOOTER //
// ./src/redux/reducers/artworks.js